/**
 * Canicule : particules ambiantes vent + chaleur (Canvas 2D léger).
 *
 * Concept : la chaleur (particules orangées) monte et stagne dans le bas du cadre ;
 * le vent (particules cyan/blanc translucides) traverse horizontalement et "repousse"
 * la chaleur — narration heat-to-fresh, continue, lente, discrète.
 *
 * Garde-fous :
 * - Aucune dépendance externe.
 * - Densité faible + opacité basse : ne nuit pas au contraste du texte.
 * - requestAnimationFrame throttlé (~30 fps), pause si document.hidden.
 * - prefers-reduced-motion : la couche est masquée par CSS et le moteur ne démarre pas.
 * - DPR géré, redimensionnement debouncé, pointer-events:none via CSS.
 */
(function () {
  'use strict';

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) return;

  var TARGET_FPS = 30;
  var FRAME_MS = 1000 / TARGET_FPS;

  // Tokens heat-to-fresh (RGB pour composer l'alpha à la volée).
  var HEAT = [
    [184, 56, 28], // orange CTA chaud
    [255, 90, 54], // orange marque
  ];
  var WIND = [
    [67, 207, 255], // cyan fraîcheur
    [230, 248, 252], // blanc glacé
  ];

  var canvas;
  var ctx;
  var wrapper;
  var dpr = Math.min(window.devicePixelRatio || 1, 2);
  var width = 0;
  var height = 0;
  var heat = [];
  var wind = [];
  var rafId = null;
  var lastFrame = 0;
  var resizeTimer = null;

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  function pick(arr) {
    return arr[(Math.random() * arr.length) | 0];
  }

  // Densité proportionnelle à l'aire, plafonnée pour rester "presque invisible".
  function targetCounts() {
    var area = width * height;
    var base = area / 26000;
    var isSmall = width < 750;
    var heatCap = isSmall ? 14 : 26;
    var windCap = isSmall ? 8 : 16;
    return {
      heat: Math.max(6, Math.min(heatCap, Math.round(base * 0.7))),
      wind: Math.max(3, Math.min(windCap, Math.round(base * 0.4))),
    };
  }

  function makeHeat() {
    var color = pick(HEAT);
    return {
      color: color,
      x: rand(0, width),
      y: rand(height * 0.45, height + 30),
      r: rand(0.8, 2.2),
      // La chaleur monte lentement (vy négatif) avec une légère dérive latérale.
      vx: rand(-0.05, 0.05),
      vy: rand(-0.28, -0.1),
      drift: rand(0, Math.PI * 2),
      driftSpeed: rand(0.004, 0.012),
      alpha: rand(0.04, 0.11),
      life: rand(0, 1),
    };
  }

  function makeWind(fromLeft) {
    var color = pick(WIND);
    var leftToRight = fromLeft === undefined ? Math.random() > 0.4 : fromLeft;
    return {
      color: color,
      x: leftToRight ? rand(-60, -10) : rand(width + 10, width + 60),
      y: rand(height * 0.15, height * 0.95),
      r: rand(1.0, 2.6),
      // Le vent traverse vite horizontalement, peu de mouvement vertical.
      vx: leftToRight ? rand(0.6, 1.5) : rand(-1.5, -0.6),
      vy: rand(-0.06, 0.06),
      len: rand(18, 52),
      alpha: rand(0.05, 0.14),
    };
  }

  function seed() {
    var counts = targetCounts();
    heat = [];
    wind = [];
    var i;
    for (i = 0; i < counts.heat; i++) heat.push(makeHeat());
    for (i = 0; i < counts.wind; i++) wind.push(makeWind());
  }

  function resize() {
    var rect = { w: window.innerWidth, h: window.innerHeight };
    width = rect.w;
    height = rect.h;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    seed();
  }

  function nearestWindPush(p) {
    // Approximation cheap : le vent global pousse la chaleur vers la droite + la haut,
    // et l'accélère quand une rafale passe à proximité verticale.
    var push = 0;
    for (var i = 0; i < wind.length; i++) {
      var w = wind[i];
      var dy = Math.abs(w.y - p.y);
      if (dy < 60) {
        push += (w.vx * (60 - dy)) / 60;
      }
    }
    return push * 0.012;
  }

  function step() {
    var i, p;

    ctx.clearRect(0, 0, width, height);

    // ── Chaleur : monte, dérive, repoussée par le vent ──
    for (i = 0; i < heat.length; i++) {
      p = heat[i];
      p.drift += p.driftSpeed;
      p.x += p.vx + Math.sin(p.drift) * 0.18 + nearestWindPush(p);
      p.y += p.vy;
      p.life += 0.003;

      // Fondu doux en fin de course puis recyclage en bas.
      var fade = 1;
      if (p.y < height * 0.2) {
        fade = Math.max(0, p.y / (height * 0.2));
      }

      if (p.y < -20 || p.x < -30 || p.x > width + 30) {
        heat[i] = makeHeat();
        heat[i].y = height + rand(4, 24);
        continue;
      }

      var c = p.color;
      var a = p.alpha * fade;
      ctx.beginPath();
      ctx.fillStyle = 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')';
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Vent : traits translucides qui traversent ──
    ctx.lineCap = 'round';
    for (i = 0; i < wind.length; i++) {
      p = wind[i];
      p.x += p.vx;
      p.y += p.vy;

      if (p.x < -80 || p.x > width + 80) {
        wind[i] = makeWind(p.vx < 0 ? false : true);
        // Relance depuis le bord opposé pour un flux continu.
        wind[i].x = p.vx > 0 ? rand(-60, -10) : rand(width + 10, width + 60);
        continue;
      }

      var wc = p.color;
      var grad = ctx.createLinearGradient(p.x - (p.vx > 0 ? p.len : -p.len), p.y, p.x, p.y);
      grad.addColorStop(0, 'rgba(' + wc[0] + ',' + wc[1] + ',' + wc[2] + ',0)');
      grad.addColorStop(1, 'rgba(' + wc[0] + ',' + wc[1] + ',' + wc[2] + ',' + p.alpha + ')');
      ctx.strokeStyle = grad;
      ctx.lineWidth = p.r;
      ctx.beginPath();
      ctx.moveTo(p.x - (p.vx > 0 ? p.len : -p.len), p.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
  }

  function loop(now) {
    rafId = window.requestAnimationFrame(loop);
    if (now - lastFrame < FRAME_MS) return;
    lastFrame = now;
    step();
  }

  function start() {
    if (rafId !== null) return;
    lastFrame = 0;
    rafId = window.requestAnimationFrame(loop);
  }

  function stop() {
    if (rafId === null) return;
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }

  function onVisibility() {
    if (document.hidden) {
      stop();
    } else {
      start();
    }
  }

  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resize, 200);
  }

  function mount() {
    if (document.querySelector('.canicule-particles')) return;

    wrapper = document.createElement('div');
    wrapper.className = 'canicule-particles';
    wrapper.setAttribute('aria-hidden', 'true');

    canvas = document.createElement('canvas');
    canvas.className = 'canicule-particles__canvas';
    wrapper.appendChild(canvas);
    document.body.appendChild(wrapper);

    ctx = canvas.getContext('2d');
    if (!ctx) {
      wrapper.parentNode.removeChild(wrapper);
      return;
    }

    resize();
    // Un premier rendu avant le fondu d'apparition.
    step();
    window.requestAnimationFrame(function () {
      wrapper.classList.add('is-ready');
    });

    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
